import { AnswerMessageStatus } from './growegyAIApi';
import { LibraryPromptContentPart } from './util';
import {
  CHAT_TYPE_BUSINESS_PLAN,
  ChatType,
  CreateProgramsArgumentDto,
  PlaceholderMetadataDto,
  PromptMetadataDto,
  UpdateBusinessPlanSectionArgumentDto,
} from './dto';
import { StyleVariables } from '../commons/styleConstants';
import { ResolvedProgramSummary } from '../program/types';
import { BusinessPlanSurveySectionGuidanceKey } from '../businessplan/types';
import { nonBreakableSpace } from '../util/utils';

export const MAX_CHAT_TITLE_LENGTH = 50;
export const MAX_CHAT_MESSAGE_LENGTH = 4000;
export const NEW_CHAT_ID = 'new-chat-id';

export type GptModel = {
  id: string;
  name: string;
  adjective: string;
  description: string;
};
export const GPT_MODEL_BASIC: GptModel = {
  id: 'GPT_BASIC',
  name: 'GPT‑4o‑mini',
  adjective: 'Basic',
  description: 'Included with Monthly plan',
};
export const GPT_MODEL_ADVANCED: GptModel = {
  id: 'GPT_ADVANCED',
  name: 'GPT‑4o',
  adjective: 'Advanced',
  description: 'The smartest and most capable model yet',
};

export type Chat = {
  id: string;
  model: GptModel;
  title: string;
  type: ChatType;
  subtype: string | null;
};

export const CLIENT_MESSAGE_SENDER = 'client' as const;
export const AI_MESSAGE_SENDER = 'ai' as const;
export type ChatMessageSender = typeof CLIENT_MESSAGE_SENDER | typeof AI_MESSAGE_SENDER;
export const MARKDOWN_SEPARATOR = '((the following text may contain markdown))';

export type ChatMessage = {
  id: string | null;
  clientId: string;
  content: string | null;
  timestamp: Date;
  sender: ChatMessageSender;
  isPlaceholder?: boolean;
  isWizardStep?: boolean;
  wizardStepNumber?: number;
  sendFailed?: boolean;
  errorMessage?: string;
  isCancelled?: boolean;
  promptId: string | null;
  toolCalls?: ToolCall[] | null;
  toolCallResults?: ToolCallResult[] | null;
  additionalContext?: string;
};

export const TOOL_CREATE_PROGRAMS = 'create_programs';
export const TOOL_UPDATE_BUSINESS_PLAN_SECTION = 'update_business_plan_section';

export type ToolCall = CreateProgramsToolCall | UpdateBusinessPlanSectionToolCall;
export type CreateProgramsToolCall = BaseToolCall & {
  name: typeof TOOL_CREATE_PROGRAMS;
  arguments: CreateProgramsArgumentDto;
};
export type UpdateBusinessPlanSectionToolCall = BaseToolCall & {
  name: typeof TOOL_UPDATE_BUSINESS_PLAN_SECTION;
  arguments: UpdateBusinessPlanSectionArgumentDto;
};

type BaseToolCall = {
  id: string;
};
export type ResolvedToolCall =
  | ResolvedCreateProgramsToolCall
  | ResolvedUpdateBusinessPlanSectionToolCall;
export type ResolvedCreateProgramsToolCall = BaseToolCall & {
  name: typeof TOOL_CREATE_PROGRAMS;
  arguments: {
    programs: ResolvedProgramSummary[];
  };
};

export type ResolvedUpdateBusinessPlanSectionToolCall = BaseToolCall & {
  name: typeof TOOL_UPDATE_BUSINESS_PLAN_SECTION;
  arguments: {
    section_text: string;
    section_key: BusinessPlanSurveySectionGuidanceKey;
  };
};

export type BaseToolCallResult = {
  id: string;
  name: typeof TOOL_CREATE_PROGRAMS | typeof TOOL_UPDATE_BUSINESS_PLAN_SECTION;
  content: {
    user_result: ToolCallUserResult;
    message?: string;
  };
};
export type ToolCallResult = CreateProgramsToolCallResult | UpdateBusinessPlanSectionToolCallResult;
export type CreateProgramToolCallResult = {
  id: string;
  name: string;
  program_type_id: string;
  all_day: boolean;
  start_date_time: string;
  end_date_time: string;
  owner_user_id: string;
};
export type CreateProgramsToolCallResult = BaseToolCallResult & {
  name: typeof TOOL_CREATE_PROGRAMS;
  content: {
    created_programs: CreateProgramToolCallResult[];
  };
};

export type UpdateBusinessPlanSectionToolCallResult = BaseToolCallResult & {
  name: typeof TOOL_UPDATE_BUSINESS_PLAN_SECTION;
  content: {
    business_plan_section_text?: string;
  };
};

export type ResolvedToolCallResult = { id: string } & (
  | ResolvedCreateProgramsToolCallResult
  | ResolvedUpdateBusinessPlanSectionToolCallResult
);
export type ResolvedCreateProgramsToolCallResult = {
  name: typeof TOOL_CREATE_PROGRAMS;
  content: {
    user_result: ToolCallUserResult;
    created_programs: ResolvedProgramSummary[];
  };
};
export type ResolvedUpdateBusinessPlanSectionToolCallResult =
  UpdateBusinessPlanSectionToolCallResult;

export type ResolvedChatMessage = ChatMessage & {
  aiMessageNumber?: number;
  resolvedToolCallResults?: ResolvedToolCallResult[] | null;
  resolvedToolCalls?: ResolvedToolCall[] | null;
};

export const TOOL_CALL_USER_RESULT_OK = 'OK';
export const TOOL_CALL_USER_RESULT_REJECT = 'REJECT';
export type ToolCallUserResult =
  | typeof TOOL_CALL_USER_RESULT_OK
  | typeof TOOL_CALL_USER_RESULT_REJECT;
export type PromptExtension =
  | {
      resolvedToolCall: ResolvedToolCall;
      userResult: typeof TOOL_CALL_USER_RESULT_OK;
    }
  | {
      toolCallId: string;
      toolCallName: string;
      userResult: typeof TOOL_CALL_USER_RESULT_REJECT;
    };

export type ChatPromptAndAnswer = {
  prompt: ChatMessage;
  promptExtensions?: PromptExtension[];
  answer: ChatMessage | null;
  answerStatus: AnswerMessageStatus;
};

export type Prompt = {
  id: string;
  title: string;
  content: string;
  categories: string[];
  metadata: PromptMetadataDto;
  parsedSpans: LibraryPromptContentPart[];
  parsedContent: string;
};

export type PromptCategory = {
  name: string;
  color: string;
  icon: string | null;
};

export type PromptVariable = {
  id: string | null;
  isPrivate: boolean;
  variableValue: PromptVariableValue;
};

export type PromptVariableBaseValue = {
  name: string;
  type: 'string' | 'list';
};

export const MAX_PROMPT_VALUE_DESCRIPTION_LENGTH = 600;

export type PromptVariableStringValue = PromptVariableBaseValue & {
  type: 'string';
  value: string;
  description: string;
};

export type PromptVariableListValue = PromptVariableBaseValue & {
  type: 'list';
  values: PromptVariableStringValue[];
};

export type PromptVariableStringValueOrAdhoc = PromptVariableStringValue & {
  kind: 'saved' | 'adhoc';
  id: string | null;
};

export type PromptVariableValue = PromptVariableStringValue | PromptVariableListValue;

export type LibraryPromptWizardStep = {
  rawPlaceholder: string;
  isFreeForm: boolean;
  question: string;
  answer?: string;
  variableName: string | null;
  valueOptions: string[] | null;
  metadata: PlaceholderMetadataDto | null;
};

export type PromptRecentUsage = {
  usageId: string | null;
  promptId: string;
  messageId: string | null;
  updatedDate: Date | null;
};

export type PromptMark = {
  promptId: string;
  mark: number;
  updatedDate: Date | null;
};

export const FALLBACK_CATEGORY_COLOR = '#64748B';

export const PREDEFINED_PROMPT_CATEGORIES: PromptCategory[] = [
  {
    name: 'Advertising',
    color: StyleVariables.cyan500,
    icon: 'shopping-bag',
  },
  {
    name: 'Blogging prompts',
    color: StyleVariables.cyan500,
    icon: 'pencil',
  },
  {
    name: 'Book Publishing',
    color: '#84CC16',
    icon: 'book',
  },
  {
    name: 'Brainstorming',
    color: '#F59E0B',
    icon: 'bulb',
  },
  {
    name: 'Content',
    color: '#22C55e',
    icon: 'pencil',
  },
  {
    name: 'Customer pain points',
    color: '#F97316',
    icon: 'grid',
  },
  {
    name: 'Email',
    color: StyleVariables.red500,
    icon: 'envelope',
  },
  {
    name: 'E-Commerce',
    color: '#F97316',
    icon: 'shopping-cart',
  },
  {
    name: 'General',
    color: '#F1F5F9',
    icon: 'grid',
  },
  {
    name: 'Marketing',
    color: '#EC4899',
    icon: 'ai-stars',
  },
  {
    name: 'Real Estate',
    color: '#14B8A6',
    icon: 'house',
  },
  {
    name: 'Rewriting paragraphs',
    color: '#06B6D4',
    icon: 'pencil',
  },
  {
    name: 'Sales',
    color: '#6366F1',
    icon: 'pie-chart',
  },
  {
    name: 'SEO',
    color: '#6366F1',
    icon: 'search',
  },
  {
    name: 'Social media',
    color: '#D946EF',
    icon: 'chat-balloon',
  },
  {
    name: 'Strategy',
    color: '#F59E0B',
    icon: 'arrow-up-right',
  },
  {
    name: 'Trends and Research',
    color: '#10B981',
    icon: 'pie-chart',
  },
  {
    name: 'Website',
    color: '#A855F7',
    icon: 'link',
  },
];

export type VariableMetadata = {
  name: string;
  isSuggested: boolean;
  isRequiredForBusinessPlan?: boolean;
  includedInOnboarding?: boolean;
  note?: string;
  appliesTo?: ChatType;
  hideName?: boolean;
  nameLabel?: string;
  descriptionLabel?: string;
  hideDescription?: boolean;
  namePlaceholder?: string;
  descriptionPlaceholder?: string;
};

export const VARIABLE_COMPANY = 'company';
export const VARIABLE_PRODUCT_OR_SERVICE = 'product or service';
export const VARIABLE_AUDIENCE = 'audience';
export const VARIABLE_INCORPORATION_TYPE = 'incorporation type';
export const VARIABLE_INCORPORATION_STATE = 'incorporation state';
export const VARIABLE_FUNDING_NEED = 'funding need';
export const VARIABLE_PERSONNEL = 'personnel';

export const VARIABLE_METADATAS: VariableMetadata[] = [
  {
    name: VARIABLE_COMPANY,
    isSuggested: true,
    isRequiredForBusinessPlan: true,
    includedInOnboarding: true,
    note: `Changing company name here has effect on Growegy AI context, but does not affect credit reporting. Company name used in credit reporting can be found in${nonBreakableSpace}Settings${nonBreakableSpace}→${nonBreakableSpace}Billing.`,
  },
  {
    name: VARIABLE_INCORPORATION_TYPE,
    isSuggested: false,
    isRequiredForBusinessPlan: true,
    appliesTo: CHAT_TYPE_BUSINESS_PLAN,
    hideName: true,
    nameLabel: 'Incorporation type',
    namePlaceholder: 'LLC, Inc or other incorporation type',
    hideDescription: true,
  },
  {
    name: VARIABLE_INCORPORATION_STATE,
    isSuggested: false,
    isRequiredForBusinessPlan: true,
    appliesTo: CHAT_TYPE_BUSINESS_PLAN,
    hideName: true,
    nameLabel: 'Incorporation state',
    namePlaceholder: 'State where the company was incorporated',
    hideDescription: true,
  },
  {
    name: VARIABLE_FUNDING_NEED,
    isSuggested: false,
    isRequiredForBusinessPlan: true,
    appliesTo: CHAT_TYPE_BUSINESS_PLAN,
    hideName: true,
    nameLabel: 'Funding need',
    namePlaceholder: 'Amount of funding needed',
    descriptionLabel: 'Funding need details',
    descriptionPlaceholder: 'Type of funding (equity, debt, etc), timeline, purpose',
  },
  {
    name: VARIABLE_PRODUCT_OR_SERVICE,
    isSuggested: true,
    isRequiredForBusinessPlan: true,
    includedInOnboarding: true,
  },
  { name: 'feature', isSuggested: true },
  {
    name: VARIABLE_AUDIENCE,
    isSuggested: true,
    isRequiredForBusinessPlan: true,
    includedInOnboarding: true,
  },
  { name: 'topic', isSuggested: false },
  { name: 'social media', isSuggested: false },
  { name: 'industry', isSuggested: false },
  { name: 'keyword', isSuggested: false },
  { name: 'value', isSuggested: false },
  {
    name: VARIABLE_PERSONNEL,
    isSuggested: false,
    isRequiredForBusinessPlan: true,
    appliesTo: CHAT_TYPE_BUSINESS_PLAN,
    nameLabel: 'Principal members',
  },
];

export type PromptVariableViewModel = PromptVariable & {
  isSuggested: boolean;
  sortOrder: number;
  isFilled: boolean;
  note: string;
  hideName?: boolean;
  nameLabel?: string;
  namePlaceholder?: string;
  descriptionLabel?: string;
  descriptionPlaceholder?: string;
  hideDescription?: boolean;
};
